import { useState, useRef, useCallback, useEffect } from 'react';
import { useUncontrolled } from '../../../hooks/useUncontrolled.js';
import { getHotkeyHandler } from '../../../utils/hotkeyHandler.js';
import { Backspace } from '../../../utils/keycode.js';
import { isArray } from '../../../utils/is.js';

const useTaggedInput = props => {
  const {
    onChange,
    taggedValue,
    inputValueProp,
    defaultValue,
    disabled,
    readOnly,
    onRemove,
    complexValue
  } = props;
  const [focus, setFocus] = useState(false);
  const [inputValue, setInputValue] = useState(inputValueProp);
  const inputRef = useRef();
  const initialValue = complexValue && isArray(taggedValue) ? [...taggedValue.map(v => v.value)] : taggedValue;
  const initialDefaultValue = complexValue && isArray(defaultValue) ? [...defaultValue.map(v => v.value)] : defaultValue;
  const [_value, setValue] = useUncontrolled({
    value: initialValue,
    defaultValue: initialDefaultValue,
    fallbackValue: [],
    onChange
  });
  const useComputeAutoWidthDelay = val => {
    const _refDelay = useRef(0);
    const refPrevValueLength = useRef(val.length);
    useEffect(() => {
      _refDelay.current = val.length === 0 && refPrevValueLength.current > 0 ? 300 : 0;
      refPrevValueLength.current = val.length;
    }, [val]);
    return _refDelay;
  };
  const refDelay = useComputeAutoWidthDelay(_value);
  const valueChangeHandler = useCallback((val, event) => {
    if (disabled || readOnly) {
      return;
    }
    if (!('value' in props)) {
      setValue && setValue(val, event);
    }
  }, [disabled, props, readOnly, setValue]);
  const tagCloseHandler = (itemValue, index, event) => {
    onRemove && onRemove(itemValue, index, event);
    valueChangeHandler([..._value.slice(0, index), ..._value.slice(index + 1)], event);
  };
  const hotkeyHandler = getHotkeyHandler(new Map([[Backspace.key, event => {
    if (!event.target.value && _value.length) {
      for (let index = _value.length - 1; index >= 0; index--) {
        const itemValue = _value[index];
        if (itemValue.closable !== false) {
          tagCloseHandler(itemValue, index, event);
          return;
        }
      }
    }
  }]]));
  const tryAddInputValueToTag = async event => {
    try {
      const isValid = inputValue && _value.every(item => item !== inputValue);
      if (isValid) valueChangeHandler(_value.concat(inputValue), event);
      setInputValue('');
    } catch (error) {
      console.error(error);
    }
  };
  return {
    focus,
    setFocus,
    inputRef,
    value: _value,
    setValue,
    valueChangeHandler,
    tagCloseHandler,
    hotkeyHandler,
    tryAddInputValueToTag,
    setInputValue,
    inputValue,
    refDelay
  };
};

export { useTaggedInput };
